import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Trophy,
  Ticket,
  DollarSign,
  TrendingUp,
  Loader2,
  Calendar,
  Target,
  Users,
  Shield
} from "lucide-react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const CommunityStatsPage = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [winningTickets, setWinningTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        axios.get(`${API}/community/winnings`),
        axios.get(`${API}/community/winning-tickets?limit=30`)
      ]);
      setData(statsRes.data);
      setWinningTickets(ticketsRes.data.tickets);
    } catch (err) {
      console.error("Failed to fetch community stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatLotteryName = (type) => {
    const names = {
      tatts_lotto: "Tatts Lotto",
      oz_lotto: "OZ Lotto",
      powerball: "Powerball",
      weekday_windfall: "Weekday Windfall",
    };
    return names[type] || type;
  };

  const formatDrawDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("T")[0].split("-");
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${days[d.getDay()]} ${dd}.${mm}.${d.getFullYear()}`;
  };

  const barChartData = data
    ? {
        labels: Object.keys(data.winnings_by_lottery).map(formatLotteryName),
        datasets: [
          {
            label: "Total Winnings ($)",
            data: Object.values(data.winnings_by_lottery).map((l) => l.total_winnings),
            backgroundColor: [
              "rgba(16, 185, 129, 0.8)",
              "rgba(59, 130, 246, 0.8)",
              "rgba(255, 215, 0, 0.8)",
              "rgba(168, 85, 247, 0.8)",
            ],
            borderColor: [
              "rgba(16, 185, 129, 1)",
              "rgba(59, 130, 246, 1)",
              "rgba(255, 215, 0, 1)",
              "rgba(168, 85, 247, 1)",
            ],
            borderWidth: 2,
            borderRadius: 8,
          },
        ],
      }
    : null;

  const barOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title: { display: true, text: "Winnings by Lottery Type", color: "#fff", font: { size: 14, weight: "600" } },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0.85)",
        callbacks: { label: (ctx) => ` ${formatCurrency(ctx.raw)}` },
      },
    },
    scales: {
      x: { ticks: { color: "#9CA3AF" }, grid: { color: "rgba(255,255,255,0.05)" } },
      y: { ticks: { color: "#9CA3AF", callback: (v) => formatCurrency(v) }, grid: { color: "rgba(255,255,255,0.05)" } },
    },
  };

  const doughnutData = data
    ? {
        labels: Object.keys(data.tickets_by_lottery).map(formatLotteryName),
        datasets: [
          {
            data: Object.values(data.tickets_by_lottery),
            backgroundColor: [
              "rgba(16, 185, 129, 0.8)",
              "rgba(59, 130, 246, 0.8)",
              "rgba(255, 215, 0, 0.8)",
              "rgba(168, 85, 247, 0.8)",
            ],
            borderColor: "#0a0a0a",
            borderWidth: 2,
          },
        ],
      }
    : null;

  const doughnutOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "right", labels: { color: "#fff", padding: 14 } },
      title: { display: true, text: "Games by Lottery", color: "#fff", font: { size: 14, weight: "600" } },
    },
  };

  if (loading) {
    return (
      <DashboardLayout currentPage="community-stats">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-aura-emerald mx-auto mb-4" />
            <p className="text-aura-text-secondary">Loading community stats...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout currentPage="community-stats">
      <div className="p-6 max-w-6xl mx-auto" data-testid="community-stats-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aura-emerald/10 border border-aura-emerald/20 text-aura-emerald text-sm font-medium mb-5">
            <Users className="w-4 h-4" />
            <span>Live Community Data</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white mb-3" data-testid="community-stats-heading">
            Live Community <span className="gradient-text">Stats</span>
          </h1>
          <p className="text-base text-aura-text-secondary max-w-xl mx-auto">
            Real-time verified results from AURA-tracked prediction games across the community.
            Manually added games are excluded.
          </p>
        </motion.div>

        {/* Verification badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-aura-emerald/5 border border-aura-emerald/15 text-aura-emerald text-xs" data-testid="verification-badge">
            <Shield className="w-3.5 h-3.5" />
            <span>Only AURA-generated prediction-based tracked games are counted</span>
          </div>
        </motion.div>

        {/* Total Winnings Hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15 }}
          className="text-center mb-10"
        >
          <div className="inline-block p-8 rounded-2xl glass-emerald" data-testid="community-total-winnings">
            <p className="text-aura-text-secondary text-base mb-2">Total Community Winnings</p>
            {data?.has_verified_wins ? (
              <p className="font-heading text-5xl md:text-7xl font-bold text-aura-gold" style={{ textShadow: "0 0 30px rgba(255,215,0,0.5)" }}>
                {formatCurrency(data.total_winnings)}
              </p>
            ) : (
              <div>
                <p className="font-heading text-3xl font-bold text-aura-gold/70" style={{ textShadow: "0 0 20px rgba(255,215,0,0.3)" }}>
                  Verifying Results...
                </p>
                <p className="text-aura-text-muted text-sm mt-3">
                  AURA is cross-checking {data?.total_valid_tickets || 0} prediction-based tickets against official results
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { icon: Ticket, value: data?.total_valid_tickets || 0, label: "Valid Prediction Games", color: "text-aura-emerald", isNumber: true },
            { icon: Trophy, value: data?.has_verified_wins ? data.total_winning_tickets : "Pending", label: "Winning Games", color: "text-aura-gold", isNumber: data?.has_verified_wins },
            { icon: DollarSign, value: data?.has_verified_wins ? formatCurrency(data.total_winnings) : "Pending", label: "Total Winnings", color: "text-green-400", isNumber: false },
            { icon: TrendingUp, value: data?.has_verified_wins ? formatCurrency(data.average_win_per_winning_ticket) : "Pending", label: "Avg Win / Game", color: "text-purple-400", isNumber: false },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.08 }}
              className="card-aura p-5 text-center"
              data-testid={`community-stat-${i}`}
            >
              <stat.icon className={`w-7 h-7 ${stat.color} mx-auto mb-3`} />
              <p className={`font-heading text-2xl font-bold ${data?.has_verified_wins || stat.isNumber ? "text-white" : "text-aura-gold/60"}`}>
                {stat.isNumber && typeof stat.value === "number" ? stat.value.toLocaleString() : stat.value}
              </p>
              <p className="text-xs text-aura-text-muted mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="card-aura p-6">
            {data?.has_verified_wins && barChartData ? (
              <div className="h-72"><Bar data={barChartData} options={barOpts} /></div>
            ) : (
              <div className="h-72 flex flex-col items-center justify-center">
                <Trophy className="w-14 h-14 text-aura-gold/30 mb-4" />
                <p className="font-heading text-lg text-aura-gold/70">Winnings by Lottery</p>
                <p className="text-aura-text-muted text-sm mt-2">Charts appear once wins are verified</p>
              </div>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="card-aura p-6">
            {data && (data.total_valid_tickets || 0) > 0 && doughnutData ? (
              <div className="h-72"><Doughnut data={doughnutData} options={doughnutOpts} /></div>
            ) : (
              <div className="h-72 flex flex-col items-center justify-center">
                <Ticket className="w-14 h-14 text-aura-emerald/30 mb-4" />
                <p className="font-heading text-lg text-aura-emerald/70">Games by Lottery</p>
                <p className="text-aura-text-muted text-sm mt-2">Distribution shows as games are tracked</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Recent Verified Wins */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="card-aura overflow-hidden">
          <div className="p-5 border-b border-white/10">
            <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-aura-emerald" />
              Recent Verified Wins
            </h2>
            <p className="text-xs text-aura-text-muted mt-1">Anonymous listing of verified community wins from AURA predictions only</p>
          </div>

          {winningTickets.length > 0 ? (
            <div className="divide-y divide-white/5">
              {winningTickets.map((ticket, i) => {
                const matchedNums = ticket.matched_numbers || [];
                const matchedSupps = ticket.matched_supps || [];
                return (
                  <div key={i} className="p-4 hover:bg-white/5 transition-colors" data-testid={`community-win-row-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-aura-gold/20 flex items-center justify-center">
                          <Trophy className="w-4 h-4 text-aura-gold" />
                        </div>
                        <div>
                          <p className="text-white text-sm font-medium">
                            {formatLotteryName(ticket.lottery_type)}
                          </p>
                          <p className="text-xs text-aura-text-muted">
                            {formatDrawDate(ticket.draw_date)} — Division {ticket.division || "N/A"} — {ticket.matches || 0} matched
                          </p>
                        </div>
                      </div>
                      <p className="font-heading text-base font-bold text-aura-gold">
                        {formatCurrency(ticket.winnings || 0)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 ml-12">
                      {(ticket.numbers || []).map((num, j) => {
                        const isMatched = matchedNums.includes(num);
                        const isSuppMatch = matchedSupps.includes(num);
                        return (
                          <span key={j} className={`lottery-ball small ${isMatched ? "winner" : isSuppMatch ? "supp-match" : ""}`}>
                            {num}
                          </span>
                        );
                      })}
                      {ticket.powerball && (
                        <span className={`lottery-ball small powerball ${ticket.powerball_match ? "winner" : ""}`}>
                          {ticket.powerball}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-10 text-center">
              <Trophy className="w-14 h-14 text-aura-gold/20 mx-auto mb-4" />
              <p className="font-heading text-lg text-aura-text-secondary mb-2">Verifying Community Results...</p>
              <p className="text-aura-text-muted text-sm">AURA is cross-checking prediction-based tickets against official draw results.</p>
            </div>
          )}
        </motion.div>

        {/* Disclaimer */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-8 text-center">
          <p className="text-xs text-aura-text-muted">
            Only AURA-generated prediction-based tracked games are included. Manually added games are excluded from all community stats.
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default CommunityStatsPage;
